import React, { useEffect } from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect, useSelector } from 'react-redux';

import { setFieldValue } from 'actions';
import type { SanitizedField } from 'models';
import type { RootState } from 'reducers/types';
import {
  getCountry,
  getFieldValue,
  isEmbedded,
  shouldShowWhatsappSubscriptions,
} from 'selectors';

import { PhoneInputField } from '../PhoneInputField/PhoneInputField';
import { CheckSMSNotification } from './CheckSMSNotification/CheckSMSNotification';
import { CheckWhatsappNotification } from './CheckWhatsappNotification/CheckWhatsappNotification';

import './SenderPhoneInputField.scss';

type SenderPhoneInputFieldProps = ConnectedProps<typeof connector> & {
  field: SanitizedField<string>;
  handleBlur: (name: string) => void;
  handleChange: (name: string, value?: string) => void;
};

const SPACE = ' ';

const SenderPhoneInputFieldComponent = ({
  countryPrefix,
  field,
  handleBlur,
  handleChange,
  isEmbedded,
  setFieldValue,
}: SenderPhoneInputFieldProps) => {
  const showCheckWhatsappNotitication = useSelector(
    shouldShowWhatsappSubscriptions,
  );

  const unsetablePrefix = (field: SanitizedField<string>) => {
    return (
      field.readOnly ||
      (field.defaultValue && !field.defaultValue.includes(' '))
    );
  };

  const phoneField = () => {
    if (unsetablePrefix(field)) {
      return field;
    }

    const [prefix, ...numberParts] = (
      field.defaultValue || `+${countryPrefix}${SPACE}`
    ).split(SPACE);
    const number = numberParts.join(' ');

    field.defaultValue = `${prefix} ${number}`;
    return field;
  };

  useEffect(() => {
    const { defaultValue } = phoneField();
    defaultValue && setFieldValue('sender_phone', defaultValue);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="SenderPhoneInputField">
      <PhoneInputField
        field={phoneField()}
        handleBlur={handleBlur}
        handleChange={handleChange}
      />
      {!isEmbedded &&
        (showCheckWhatsappNotitication ? (
          <CheckWhatsappNotification />
        ) : (
          <CheckSMSNotification />
        ))}
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const id = getFieldValue(state, 'sender_country');

  return {
    countryPrefix: getCountry(state, id as string).dialingCode,
    isEmbedded: isEmbedded(state),
  };
};

const mapDispatchToProps = {
  setFieldValue,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const SenderPhoneInputField = connector(SenderPhoneInputFieldComponent);

export { SenderPhoneInputField };
